<template>
    <div class="ask-contact-form bg-white rounded-[10px] px-5 py-5 sm:px-7 sm:py-6 border border-[#E2E2E2]">
        <form v-if="!sent" ref="form" method="POST" :action="action" class="flex flex-col gap-3 pb-2" novalidate
              @submit.prevent="onSubmit">
            <div v-for="field in textFields" :key="field.name">
                <input v-model="formData[field.name]" :type="field.type" :name="field.name"
                       :autocomplete="field.autocomplete" :placeholder="$t('ask-contact.fields.' + field.name)"
                       :aria-label="$t('ask-contact.fields.' + field.name)" :disabled="submitting"
                       class="ac-control" :class="{ 'ac-invalid': !!errors[field.name] }" required>
                <p v-if="errors[field.name]" class="ac-error">{{ errors[field.name][0] }}</p>
            </div>

            <phone-field v-model="formData.phone" name="phone" :errors="errors.phone"
                         :label="$t('ask-contact.fields.phone')" :show-label="false" :country-id="countryId"
                         :disabled="submitting ? 'disabled' : null" required new-version
                         :class="{ 'ac-invalid': !!errors.phone }"></phone-field>

            <p class="text-base text-[#273B64] mt-1" id="ask-contact-meeting-type">
                {{ $t('ask-contact.fields.meeting_type') }}
            </p>
            <div class="flex gap-2" role="radiogroup" aria-labelledby="ask-contact-meeting-type">
                <button v-for="type in meetingTypes" :key="type" type="button" role="radio"
                        :aria-checked="formData.meeting_type === type ? 'true' : 'false'"
                        class="ac-choice" :class="{ 'is-selected': formData.meeting_type === type }"
                        :disabled="submitting" @click="formData.meeting_type = type">
                    {{ $t('ask-contact.meeting-types.' + type) }}
                </button>
            </div>
            <p v-if="errors.meeting_type" class="ac-error">{{ errors.meeting_type[0] }}</p>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
                <div class="relative" ref="datePicker">
                    <button type="button" class="ac-control ac-trigger" :class="{ 'ac-invalid': !!errors.date }"
                            :aria-expanded="dateOpen ? 'true' : 'false'" :disabled="submitting" @click="toggleDate">
                        <span v-if="formData.date" class="text-[#273B64]">{{ dateLabel }}</span>
                        <span v-else class="text-[#989898]">{{ $t('ask-contact.fields.date') }}</span>
                        <span class="text-[#00A07F]">
                            <svg xmlns="http://www.w3.org/2000/svg" class="inline-block shrink-0 align-[-0.125em]" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z"></path><path d="M16 3v4"></path><path d="M8 3v4"></path><path d="M4 11h16"></path></svg>
                        </span>
                    </button>
                    <p v-if="errors.date" class="ac-error">{{ errors.date[0] }}</p>
                    <div v-if="dateOpen" class="ac-popover">
                        <div class="flex items-center justify-between mb-2 text-[15px]">
                            <button type="button" class="ac-nav" :disabled="!canGoBack"
                                    :aria-label="$t('ask-contact.calendar.previous-month')" @click="shiftMonth(-1)">
                                <svg xmlns="http://www.w3.org/2000/svg" class="inline-block shrink-0 align-[-0.125em]" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6l6 6"></path></svg>
                            </button>
                            <strong class="font-semibold">{{ monthLabel }}</strong>
                            <button type="button" class="ac-nav" :aria-label="$t('ask-contact.calendar.next-month')"
                                    @click="shiftMonth(1)">
                                <svg xmlns="http://www.w3.org/2000/svg" class="inline-block shrink-0 align-[-0.125em]" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6l-6 6"></path></svg>
                            </button>
                        </div>
                        <div class="grid grid-cols-7 gap-0.5 mb-1">
                            <span v-for="(initial, index) in weekdayInitials" :key="index"
                                  class="text-center text-[12px] font-semibold"
                                  :class="index >= 5 ? 'text-[#C2C2C2]' : 'text-[#989898]'">{{ initial }}</span>
                        </div>
                        <div class="grid grid-cols-7 gap-0.5 text-[14px] tabular-nums">
                            <template v-for="(day, index) in days">
                                <span v-if="day.blank" :key="'blank-' + index"></span>
                                <span v-else-if="day.off" :key="day.value"
                                      class="h-8 flex items-center justify-center rounded-full text-[#C2C2C2] bg-[#F5F5F5] line-through">{{ day.n }}</span>
                                <button v-else :key="day.value" type="button"
                                        class="ac-day h-8 flex items-center justify-center rounded-full"
                                        :class="day.value === formData.date ? 'bg-[#00A07F] text-white font-semibold' : 'text-[#273B64] hover:bg-[#E6F5F1]'"
                                        :aria-pressed="day.value === formData.date ? 'true' : 'false'"
                                        @click="pickDate(day.value)">{{ day.n }}</button>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="relative" ref="timePicker">
                    <button type="button" class="ac-control ac-trigger" :class="{ 'ac-invalid': !!errors.time }"
                            :aria-expanded="timeOpen ? 'true' : 'false'" :disabled="submitting" @click="toggleTime">
                        <span v-if="formData.time" class="text-[#273B64]">{{ timeLabel }}</span>
                        <span v-else class="text-[#989898]">{{ $t('ask-contact.fields.time') }}</span>
                        <span class="text-[#00A07F]">
                            <svg xmlns="http://www.w3.org/2000/svg" class="inline-block shrink-0 align-[-0.125em]" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path><path d="M12 7v5l3 3"></path></svg>
                        </span>
                    </button>
                    <p v-if="errors.time" class="ac-error">{{ errors.time[0] }}</p>
                    <div v-if="timeOpen" class="ac-popover">
                        <p class="text-[13px] text-[#989898] mb-2">{{ $t('ask-contact.time-hint') }}</p>
                        <div class="grid grid-cols-3 gap-1.5 tabular-nums">
                            <template v-for="slot in timeSlots">
                                <span v-if="!slot.available" :key="slot.value"
                                      class="py-1.5 text-center rounded-md bg-[#F5F5F5] text-[#C2C2C2] text-[14px] line-through">{{ slot.label }}</span>
                                <button v-else :key="slot.value" type="button"
                                        class="ac-slot py-1.5 text-center rounded-md text-[14px]"
                                        :class="slot.value === formData.time ? 'bg-[#00A07F] text-white font-semibold border border-[#00A07F]' : 'border border-[#E2E2E2] text-[#273B64] hover:border-[#00A07F]'"
                                        :aria-pressed="slot.value === formData.time ? 'true' : 'false'"
                                        @click="pickTime(slot.value)">{{ slot.label }}</button>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <textarea v-model="formData.needs" name="needs" rows="3" :placeholder="$t('ask-contact.placeholders.needs')"
                          :aria-label="$t('ask-contact.fields.needs')" :disabled="submitting"
                          class="ac-control ac-textarea" :class="{ 'ac-invalid': !!errors.needs }"></textarea>
                <p v-if="errors.needs" class="ac-error">{{ errors.needs[0] }}</p>
            </div>

            <p class="text-[15px] text-[#6b7891] text-center mt-1">{{ $t('ask-contact.notice') }}</p>
            <p v-if="genericError" class="ac-error text-center" role="alert">{{ genericError }}</p>
            <button type="submit" class="ac-submit" :disabled="submitting">
                {{ submitting ? $t('ask-contact.submitting') : $t('ask-contact.submit') }}
            </button>
        </form>

        <div v-else class="flex flex-col items-center text-center gap-3 py-6" role="status">
            <span class="text-[#00A07F] text-[40px]">
                <svg xmlns="http://www.w3.org/2000/svg" class="inline-block" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path><path d="M9 12l2 2l4 -4"></path></svg>
            </span>
            <h2 class="text-[22px] font-semibold text-[#273B64]">{{ $t('ask-contact.sent.title') }}</h2>
            <p class="text-[17px] text-[#4a5a7a]">{{ $t('ask-contact.sent.content', sentSummary) }}</p>
            <div class="w-full flex flex-col items-center gap-3 mt-3 pt-5 border-t border-[#E2E2E2]">
                <p class="text-[17px] text-[#273B64]">{{ $t('ask-contact.sent.meanwhile') }}</p>
                <a :href="registrationUrl" class="ac-submit ac-register">{{ $t('ask-contact.sent.simulate') }}</a>
            </div>
            <a :href="continueUrl" class="ac-back">{{ $t('ask-contact.sent.back') }}</a>
        </div>
    </div>
</template>

<script>
import PhoneField from './widgets/PhoneFieldComponent.vue';

const FIELDS = ['name', 'company', 'email', 'phone', 'meeting_type', 'date', 'time', 'needs'];

function pad(value) {
    return String(value).padStart(2, '0');
}

function toIsoDate(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
}

function capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

function emptyErrors() {
    return FIELDS.reduce((errors, field) => Object.assign(errors, { [field]: null }), {});
}

export default {
    components: {
        PhoneField,
    },
    props: {
        action: String,
        locale: String,
        countryId: String,
        timeSlotsString: String,
        continueUrl: String,
        registrationUrl: String,
    },
    data() {
        // Premier mois proposé : celui du premier jour réservable (demain au plus tôt).
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        return {
            meetingTypes: ['visio', 'phone'],
            textFields: [
                { name: 'name', type: 'text', autocomplete: 'name' },
                { name: 'company', type: 'text', autocomplete: 'organization' },
                { name: 'email', type: 'email', autocomplete: 'email' },
            ],
            formData: {
                name: '',
                company: '',
                email: '',
                phone: '',
                meeting_type: 'visio',
                date: null,
                time: null,
                needs: '',
            },
            errors: emptyErrors(),
            genericError: null,
            submitting: false,
            sent: false,
            sentSummary: {},
            dateOpen: false,
            timeOpen: false,
            viewYear: tomorrow.getFullYear(),
            viewMonth: tomorrow.getMonth(),
        };
    },
    computed: {
        timeSlots() {
            return JSON.parse(this.timeSlotsString || '[]');
        },
        today() {
            const today = new Date();
            return toIsoDate(today);
        },
        canGoBack() {
            const now = new Date();
            return this.viewYear > now.getFullYear()
                || (this.viewYear === now.getFullYear() && this.viewMonth > now.getMonth());
        },
        monthLabel() {
            const label = new Date(this.viewYear, this.viewMonth, 1)
                .toLocaleDateString(this.locale, { month: 'long', year: 'numeric' });
            return capitalize(label);
        },
        weekdayInitials() {
            // 1er janvier 2024 : un lundi.
            return [0, 1, 2, 3, 4, 5, 6].map(offset => new Date(2024, 0, 1 + offset)
                .toLocaleDateString(this.locale, { weekday: 'narrow' }));
        },
        days() {
            const firstWeekday = (new Date(this.viewYear, this.viewMonth, 1).getDay() + 6) % 7;
            const dayCount = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
            const days = [];
            for (let i = 0; i < firstWeekday; i++) {
                days.push({ blank: true });
            }
            for (let n = 1; n <= dayCount; n++) {
                const date = new Date(this.viewYear, this.viewMonth, n);
                const value = toIsoDate(date);
                const weekday = date.getDay();
                days.push({ n, value, off: weekday === 0 || weekday === 6 || value <= this.today });
            }
            return days;
        },
        dateText() {
            if (!this.formData.date) {
                return '';
            }
            const [year, month, day] = this.formData.date.split('-').map(Number);
            return new Date(year, month - 1, day)
                .toLocaleDateString(this.locale, { weekday: 'long', day: 'numeric', month: 'long' });
        },
        dateLabel() {
            return capitalize(this.dateText);
        },
        timeLabel() {
            const slot = this.timeSlots.find(s => s.value === this.formData.time);
            return slot ? slot.label : '';
        },
    },
    mounted() {
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
    },
    methods: {
        toggleDate() {
            this.dateOpen = !this.dateOpen;
            this.timeOpen = false;
        },
        toggleTime() {
            this.timeOpen = !this.timeOpen;
            this.dateOpen = false;
        },
        shiftMonth(step) {
            const month = new Date(this.viewYear, this.viewMonth + step, 1);
            this.viewYear = month.getFullYear();
            this.viewMonth = month.getMonth();
        },
        pickDate(value) {
            this.formData.date = value;
            this.errors.date = null;
            this.dateOpen = false;
        },
        pickTime(value) {
            this.formData.time = value;
            this.errors.time = null;
            this.timeOpen = false;
        },
        onDocumentClick(event) {
            if (this.dateOpen && this.$refs.datePicker && !this.$refs.datePicker.contains(event.target)) {
                this.dateOpen = false;
            }
            if (this.timeOpen && this.$refs.timePicker && !this.$refs.timePicker.contains(event.target)) {
                this.timeOpen = false;
            }
        },
        onKeydown(event) {
            if (event.key === 'Escape') {
                this.dateOpen = false;
                this.timeOpen = false;
            }
        },
        onSubmit() {
            this.errors = emptyErrors();
            this.genericError = null;
            this.submitting = true;
            axios.post(this.action, this.formData)
                .then(() => {
                    this.submitting = false;
                    this.sentSummary = { date: this.dateText, time: this.timeLabel };
                    this.sent = true;
                    if (window.dataLayer) {
                        window.dataLayer.push({
                            'event': 'ask_contact_submit',
                            'event_category': 'Ask Contact',
                            'meeting_type': this.formData.meeting_type,
                        });
                    }
                    if (typeof window.matomoTrackEvent === 'function') {
                        window.matomoTrackEvent('Ask Contact', 'Submit', this.formData.meeting_type);
                    }
                })
                .catch((error) => {
                    this.submitting = false;
                    const response = error.response;
                    if (response && response.status === 422 && response.data && response.data.errors) {
                        this.errors = Object.assign(emptyErrors(), response.data.errors);
                    } else {
                        this.genericError = this.$t('ask-contact.errors.generic');
                    }
                });
        },
    },
};
</script>

<style lang="scss">
@import "~styles/_variables";

// Styles maquette « LP Pro — RDV visio v2 », limités au formulaire /ask-contact.
.ask-contact-form {
    font-family: inherit;
    color: $primary-color-dark;

    .ac-control {
        display: block;
        width: 100%;
        padding: 12px 14px;
        background-color: white;
        border: 1px solid #E2E2E2;
        border-radius: 6px;
        color: $primary-color-dark;
        font-family: inherit;
        font-size: 16px;
        line-height: 1.5;
        transition: border-color .15s ease;

        &::placeholder {
            color: #989898;
            opacity: 1;
        }

        &:hover,
        &:focus {
            border-color: $primary-color;
        }

        &:focus {
            outline: none;
            box-shadow: none;
        }

        &.ac-invalid {
            border-color: #D95E5A;
        }
    }

    .ac-trigger {
        display: flex;
        align-items: center;
        justify-content: space-between;
        text-align: left;
        cursor: pointer;
    }

    .ac-textarea {
        height: 88px;
        resize: vertical;
    }

    .ac-error {
        margin: 4px 0 0 2px;
        color: #D95E5A;
        font-size: 12px;
        line-height: 1.4;
    }

    .ac-choice {
        flex: 1 1 0;
        padding: 10px;
        background-color: white;
        border: 1px solid #C2C2C2;
        border-radius: 6px;
        color: #989898;
        font-family: inherit;
        font-size: 16px;
        line-height: 1.5;
        text-align: center;
        cursor: pointer;
        transition: border-color .15s ease, color .15s ease;

        &:hover {
            border-color: $primary-color-dark;
            color: $primary-color-dark;
        }

        &.is-selected {
            padding: 9.5px;
            border: 1.5px solid $primary-color-dark;
            color: $primary-color-dark;
            font-weight: 600;
        }
    }

    .ac-popover {
        position: absolute;
        z-index: 30;
        top: calc(100% + 6px);
        left: 0;
        right: 0;
        padding: 12px;
        background-color: white;
        border: 1px solid #E2E2E2;
        border-radius: 6px;
        box-shadow: 0 8px 24px -12px rgba(14, 30, 60, .35);
    }

    .ac-nav {
        padding: 4px;
        background: transparent;
        border: 0;
        color: $primary-color;
        cursor: pointer;

        &:disabled {
            color: #C2C2C2;
            cursor: default;
        }
    }

    .ac-day,
    .ac-slot {
        font-family: inherit;
        cursor: pointer;
    }

    .ac-day {
        border: 0;
    }

    .ac-submit {
        width: 100%;
        padding: 14px;
        background-color: #D95E5A;
        border: 0;
        border-radius: 9px;
        color: white;
        font-family: inherit;
        font-size: 18px;
        font-weight: 600;
        line-height: 1.5;
        cursor: pointer;
        transition: background-color .15s ease;

        &:hover {
            background-color: darken(#D95E5A, 6%);
        }

        &:disabled {
            opacity: .7;
            cursor: default;
        }
    }

    .ac-register {
        display: block;
        max-width: 360px;
        text-align: center;

        // Lien habillé en bouton : sans la seconde classe, `.md-theme-default a:not(.md-button)` (Vue Material et
        // md-overrides.scss) l'emporte, repeint le texte en vert et le souligne.
        &.ac-submit,
        &.ac-submit:hover {
            color: white;
            text-decoration: none;
        }
    }

    .ac-back {
        margin-top: 4px;
        color: $primary-color;
        font-size: 16px;
        font-weight: 600;
        text-decoration: underline;

        &:hover {
            color: $primary-color-dark;
        }
    }

    // Indicatif + numéro, encadrés comme un seul champ.
    .PhoneFieldComponent {
        > .flex {
            background-color: white;
            border: 1px solid #E2E2E2;
            border-radius: 6px;
            transition: border-color .15s ease;

            &:hover,
            &:focus-within {
                border-color: $primary-color;
            }
        }

        .md-menu {
            padding-left: 4px;
            background-color: transparent;
            border: 0;
            border-right: 1px solid #E2E2E2;
            border-radius: 6px 0 0 6px;
        }

        .nv-button {
            height: 100%;
            margin: 0;
            color: $primary-color-dark;
            font-size: 16px;
        }

        input {
            padding: 12px 14px;
            border: 0;
            border-radius: 0 6px 6px 0;
            font-family: inherit;
            font-size: 16px;
            line-height: 1.5;

            &::placeholder {
                color: #989898;
                font-style: normal;
                opacity: 1;
            }
        }

        .text-red-500 {
            margin: 4px 0 0 2px;
            color: #D95E5A;
            font-size: 12px;
            line-height: 1.4;
        }
    }

    .ac-invalid.PhoneFieldComponent > .flex {
        border-color: #D95E5A;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/AskContactFormComponent.vue